import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  User, Mail, Phone, MapPin, Briefcase, Calendar, 
  Shield, GraduationCap, FileText, Edit, Save, RefreshCw,
  Building, Clock, CheckCircle, AlertTriangle, Upload
} from 'lucide-react';
import { PersonalDocumentsManager } from '../components/PersonalDocumentsManager';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyProfilePage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/staff/my-profile');
      setProfile(response.data);
      setEditForm({
        phone: response.data.phone || '',
        address: response.data.address || '',
        emergency_contact_name: response.data.emergency_contact_name || '',
        emergency_contact_phone: response.data.emergency_contact_phone || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load your profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put('/staff/my-profile', editForm);
      toast.success('Profile updated successfully');
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      director: 'bg-purple-100 text-purple-800',
      therapist: 'bg-blue-100 text-blue-800',
      professional: 'bg-cyan-100 text-cyan-800',
      clinical_lead: 'bg-teal-100 text-teal-800',
      educational_lead: 'bg-green-100 text-green-800',
      finance: 'bg-amber-100 text-amber-800',
      admin: 'bg-slate-100 text-slate-800',
      hr: 'bg-pink-100 text-pink-800',
      support: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-slate-100 text-slate-800';
  };

  const formatRole = (role) => {
    const roleNames = {
      director: 'Director',
      therapist: 'Therapist',
      professional: 'External Professional',
      clinical_lead: 'Clinical Lead',
      educational_lead: 'Educational Lead',
      finance: 'Finance',
      admin: 'Admin Staff',
      hr: 'HR Staff',
      support: 'Reception/Support'
    };
    return roleNames[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-slate-500">Unable to load your profile</p>
        <Button onClick={fetchProfile} className="mt-4">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-profile-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <User className="w-8 h-8" />
            My Profile
          </h1>
          <p className="text-slate-500 mt-1">
            View and update your personal information
          </p>
        </div>
        <Button 
          onClick={() => editMode ? handleSaveProfile() : setEditMode(true)}
          className={editMode ? 'bg-green-600 hover:bg-green-700' : 'bg-[#1e3a5f]'}
        >
          {editMode ? (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          ) : (
            <>
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <Avatar className="w-24 h-24 mx-auto mb-4">
                {(profile.profile_image || profile.profile_image_data) && (
                  <AvatarImage 
                    src={profile.profile_image_data || `${API_URL}${profile.profile_image}`}
                    alt={`${profile.first_name} ${profile.last_name}`}
                  />
                )}
                <AvatarFallback className="bg-[#1e3a5f] text-white text-2xl">
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold text-slate-900">
                {profile.first_name} {profile.last_name}
              </h2>
              <Badge className={`${getRoleBadgeColor(profile.role)} mt-2`}>
                {formatRole(profile.role)}
              </Badge>
              {profile.specialty && (
                <p className="text-sm text-slate-500 mt-2">{profile.specialty}</p>
              )}
              <div className="mt-4 pt-4 border-t space-y-2 text-left">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span>{profile.email}</span>
                </div>
                {profile.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{profile.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>Joined: {profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-GB') : 'N/A'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Your contact details and emergency information</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact Information
                </h3>
                
                <div>
                  <Label className="text-slate-500">Email Address</Label>
                  <p className="font-medium">{profile.email}</p>
                </div>
                
                <div>
                  <Label className="text-slate-500">Phone Number</Label>
                  {editMode ? (
                    <Input 
                      value={editForm.phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <p className="font-medium">{profile.phone || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-slate-500">Address</Label>
                  {editMode ? (
                    <Input 
                      value={editForm.address}
                      onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Enter address"
                    />
                  ) : (
                    <p className="font-medium">{profile.address || 'Not provided'}</p>
                  )}
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Emergency Contact
                </h3>
                
                <div>
                  <Label className="text-slate-500">Contact Name</Label>
                  {editMode ? (
                    <Input 
                      value={editForm.emergency_contact_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                      placeholder="Emergency contact name"
                    />
                  ) : (
                    <p className="font-medium">{profile.emergency_contact_name || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-slate-500">Contact Phone</Label>
                  {editMode ? (
                    <Input 
                      value={editForm.emergency_contact_phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                      placeholder="Emergency contact phone"
                    />
                  ) : (
                    <p className="font-medium">{profile.emergency_contact_phone || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>

            {editMode && (
              <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={() => { setEditMode(false); fetchProfile(); }}>
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Employment Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Employment Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <Label className="text-slate-500">Employee ID</Label>
              <p className="font-medium">{profile.employee_id || profile.id?.slice(0, 8)}</p>
            </div>
            <div>
              <Label className="text-slate-500">Role</Label>
              <p className="font-medium">{formatRole(profile.role)}</p>
            </div>
            <div>
              <Label className="text-slate-500">Specialty</Label>
              <p className="font-medium">{profile.specialty || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Employment Status</Label>
              <Badge className={profile.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {profile.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Info */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Privacy Notice:</strong> Your personal information is protected and only accessible by authorized HR personnel and management.
          Contact HR if you need to update information that you cannot edit here.
        </AlertDescription>
      </Alert>

      {/* Personal Documents Section */}
      <PersonalDocumentsManager 
        userId={profile.id} 
        userName={`${profile.first_name} ${profile.last_name}`}
        onProfilePhotoUpdated={fetchProfile}
      />
    </div>
  );
}
